import React from "react";
import { Badge } from "@/components/ui/badge";
import {
  ClipboardText, User, IdentificationCard, EnvelopeSimple, MapPin,
  Car, Wrench, CalendarBlank, Clock, Buildings, Package, Phone, Wrench as ToolIcon,
} from "@phosphor-icons/react";

/**
 * Structured form summary card shown at the top of the Inbox conversation view.
 * Populated when a customer submitted an inline form via the web widget
 * ([[FORM:appointment | sales_lead | parts_quote]]).
 *
 * Feb 2026 (Sprint 30B — item #8 "resumen estructurado dentro de la consola del agente").
 */
const TYPE_META = {
  appointment: {
    title: "Cita de Taller",
    color: "bg-blue-500/10 text-blue-700 dark:text-blue-300 border-blue-500/40",
    icon: Wrench,
  },
  sales_lead: {
    title: "Lead Comercial",
    color: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/40",
    icon: Car,
  },
  parts_quote: {
    title: "Cotización de Repuestos",
    color: "bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/40",
    icon: Package,
  },
};

function Field({ icon: Icon, label, value, required }) {
  const missing = required && !value;
  if (!required && !value) return null;
  return (
    <div className="flex items-start gap-1.5 text-xs" data-testid={missing ? `field-pending-${label}` : undefined}>
      <Icon size={12} weight="duotone" className={missing ? "text-rose-500 shrink-0 mt-0.5" : "text-muted-foreground shrink-0 mt-0.5"} />
      <span className="text-muted-foreground shrink-0">{label}:</span>
      {missing ? (
        <span className="text-[10px] font-bold uppercase tracking-wider text-rose-600 bg-rose-500/10 border border-rose-500/40 rounded px-1 py-0.5">
          Pendiente
        </span>
      ) : (
        <span className="font-medium text-foreground truncate">{value}</span>
      )}
    </div>
  );
}

export default function FormSummaryCard({ formType, data, leadId, onOpenLead }) {
  if (!formType || !data) return null;
  const meta = TYPE_META[formType] || TYPE_META.sales_lead;
  const Icon = meta.icon;
  const vehicle = [data.brand, data.model, data.year].filter(Boolean).join(" ");
  const docLabel = (data.doc_type || "Documento").toUpperCase();

  // Required fields per form type — used to badge "Pendiente" on missing values.
  const REQUIRED_BY_TYPE = {
    appointment: ["name", "document", "brand", "model", "year", "appointment_type", "branch", "preferred_date", "preferred_time"],
    sales_lead:  ["name", "last_name", "phone", "email", "brand", "model", "branch"],
    parts_quote: ["name", "document", "brand", "model", "year", "chassis", "part_description"],
  };
  const req = new Set(REQUIRED_BY_TYPE[formType] || []);
  const R = (k) => req.has(k);

  // Count pending fields for the summary badge
  const pendingCount = [...req].filter(k => !data[k]).length;

  return (
    <div
      className={`m-3 p-3 rounded-lg border ${meta.color}`}
      data-testid={`form-summary-${formType}`}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-md bg-white/50 dark:bg-black/20 flex items-center justify-center">
            <Icon size={14} weight="fill" />
          </div>
          <div className="font-bold text-sm">{meta.title}</div>
          <Badge variant="outline" className="text-[9px] gap-1">
            <ClipboardText size={9} /> FORMULARIO
          </Badge>
          {pendingCount > 0 && (
            <Badge className="text-[9px] gap-1 bg-rose-500/15 text-rose-700 dark:text-rose-300 border-rose-500/40 border" data-testid="form-summary-pending-badge">
              {pendingCount} pendiente{pendingCount === 1 ? "" : "s"}
            </Badge>
          )}
        </div>
        {leadId && (
          <button
            onClick={() => onOpenLead?.(leadId)}
            data-testid={`form-summary-open-lead-${leadId}`}
            className="text-[10px] font-semibold underline decoration-dotted hover:opacity-80"
          >
            Editar en Leads →
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-1">
        <Field icon={User} label="Nombre" value={formType === "sales_lead" ? [data.name, data.last_name].filter(Boolean).join(" ") || null : data.name} required={R("name")} />
        {formType === "sales_lead" && <Field icon={Phone} label="Teléfono" value={data.phone} required />}
        <Field icon={IdentificationCard} label={docLabel} value={data.document} required={R("document")} />
        <Field icon={EnvelopeSimple} label="Email" value={data.email} required={R("email")} />
        <Field icon={MapPin} label="Provincia" value={data.provincia} required={R("provincia")} />
        <Field icon={Car} label="Vehículo" value={vehicle || null} required={R("brand") || R("model") || R("year")} />
        {formType === "sales_lead" && <Field icon={Buildings} label="Sucursal" value={data.branch} required />}

        {formType === "appointment" && (
          <>
            <Field icon={ToolIcon} label="Tipo cita" value={data.appointment_type} required={R("appointment_type")} />
            <Field icon={Buildings} label="Taller" value={data.branch} required={R("branch")} />
            <Field icon={CalendarBlank} label="Día" value={data.preferred_date} required={R("preferred_date")} />
            <Field icon={Clock} label="Hora" value={data.preferred_time} required={R("preferred_time")} />
          </>
        )}

        {formType === "parts_quote" && (
          <>
            <Field icon={ToolIcon} label="Chasis" value={data.chassis} required={R("chassis")} />
            <Field icon={Package} label="Nº pieza" value={data.part_number} />
            <div className="col-span-full">
              <Field icon={ClipboardText} label="Descripción" value={data.part_description} required={R("part_description")} />
            </div>
          </>
        )}
      </div>
    </div>
  );
}
